import { Checkbox } from '@/components/ui/checkbox'
import { formatCurrency } from '@/utils'
import type { GridVersionPayload } from 'honeygrid-types'
import { CircleIcon } from 'lucide-react'

import type { ColumnDef } from '@tanstack/react-table'
import type { DataSourceFieldMappings } from 'honeygrid-types/validators-and-types/DataSourceTypes'
import type { StatsTableData } from './StatsTable'

interface TargetingColumnProps {
	targetingSelection?: string[]
	manualInclusionFilters?: GridVersionPayload['manual_inclusion_filters']
	manualExclusionFilters?: GridVersionPayload['manual_exclusion_filters']
	fieldMappings?: DataSourceFieldMappings
}

export const getColumns = ({
	targetingSelection,
	manualInclusionFilters,
	manualExclusionFilters,
	customKeys = [],
	fieldMappings = {},
}: TargetingColumnProps & { customKeys?: string[] }): Array<ColumnDef<StatsTableData, unknown>> => {
	const columns: Array<ColumnDef<StatsTableData, unknown>> = [
		{
			id: 'select',
			header: ({ table }) => (
				<Checkbox
					checked={table.getIsAllPageRowsSelected()}
					onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
					aria-label="Select all"
					className="translate-y-[2px]"
				/>
			),
			cell: ({ row }) => (
				<Checkbox
					checked={row.getIsSelected()}
					onCheckedChange={(value) => row.toggleSelected(!!value)}
					aria-label="Select row"
					className="translate-y-[2px]"
				/>
			),
			enableSorting: false,
			enableHiding: false,
		},
		{
			accessorKey: 'id',
			header: 'ID',
			cell: ({ row }) => {
				const id = row.getValue('id') as string
				return <div>{id}</div>
			},
			enableHiding: true,
			enableSorting: false,
		},
		{
			id: 'targeting',
			header: 'Targeting',
			cell: ({ row }) => {
				const cellId = (row.getValue('id') as number).toString()
				const colors = []
				if (manualExclusionFilters?.includes(cellId)) {
					colors.push('text-destructive')
				}
				if (targetingSelection?.includes(cellId) || manualInclusionFilters?.includes(cellId)) {
					colors.push('text-primary')
				}
				return (
					<div className="flex gap-1">
						{colors.map((color) => (
							<CircleIcon key={cellId} className={`h-4 w-4 ${color}`} fill="currentColor" />
						))}
					</div>
				)
			},
			enableSorting: false,
		},
	]

	// Add custom data columns if they exist
	const customerDataColumns: Record<string, ColumnDef<StatsTableData, unknown>> = {
		customerCount: {
			accessorKey: 'customerCount',
			header: 'Customer Count',
		},
		sales: {
			accessorKey: 'sales',
			header: 'Sales',
		},
		customerMarketShare: {
			accessorKey: 'customerMarketShare',
			header: 'Market Share',
		},
	}

	customKeys.forEach((key) => {
		if (customerDataColumns[key]) {
			columns.push(customerDataColumns[key])
		} else {
			columns.push({
				accessorKey: key,
				header: fieldMappings?.[key]?.friendlyName || key,
			})
		}
	})

	// Add generic columns
	const genericColumns: Array<ColumnDef<StatsTableData, unknown>> = [
		{
			accessorKey: 'households',
			header: 'Population',
		},
		{
			accessorKey: 'newMovers',
			header: 'New Movers',
		},
		{
			accessorKey: 'income',
			header: 'Income',
			cell: ({ row }) => {
				const value = row.getValue('income')
				return value ? formatCurrency(Number(value)) : null
			},
		},
		{
			accessorKey: 'homeValues',
			header: 'Home Values',
			cell: ({ row }) => {
				const value = row.getValue('homeValues')
				return value ? formatCurrency(Number(value)) : null
			},
		},
		{
			accessorKey: 'sfdu',
			header: 'SFDUs',
		},
		{
			accessorKey: 'mfdu',
			header: 'MFDUs',
		},
	]

	columns.push(...genericColumns)

	return columns
}
